/**
 * Block composer for the AGNT home page.
 *
 * This is the page's "LEGO baseplate": it owns the section order and wires each
 * slice of `HOME_CONTENT` to its block component. Reordering, adding, or
 * swapping a section is a one-line change here — the blocks themselves are
 * self-contained (own markup + `*.module.scss`) and know nothing about their
 * neighbours. Sections opt into scroll-reveal via `data-reveal`; the page
 * wrapper (`./index`) runs the observer and owns the reveal CSS.
 */

import { useRef } from 'react';

import Hero from './sections/hero';
import ResultsSummary from './sections/results-summary';
import FounderQuote from './sections/founder-quote';
import InnovationInvestor from './sections/innovation-investor';
import News from './sections/news';
import CareersIntro from './sections/careers-intro';
import CalendarAlerts from './sections/calendar-alerts';

import CardStack from 'components/agnt-inc/card-stack';
import useScrollReveal from 'components/agnt-inc/use-scroll-reveal';

import styles from './blocks.module.scss';

import type { HomeContent, NewsCard } from './content';

interface HomeBlocksProps {
  content: HomeContent;
  /** Server-fetched News cards; falls back to `content.news.cards` when empty. */
  newsCards?: NewsCard[];
  /** Live AGNT stock price (e.g. `$6.82`) for the Stock Information row. */
  stockPrice?: string | null;
}

export default function HomeBlocks({ content, newsCards, stockPrice }: HomeBlocksProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  useScrollReveal(rootRef);

  return (
    <div ref={rootRef} className={styles.home}>
      <Hero content={content.hero} />
      <ResultsSummary content={content.resultsSummary} />
      <FounderQuote content={content.founderQuote} />
      <InnovationInvestor content={content.innovationInvestor} stockPrice={stockPrice} />
      {newsCards?.length ? (
        <News content={content.news} cards={newsCards} />
      ) : null}
      <div className={styles.careersWrapper}>
        <CareersIntro content={content.careersIntro} />
        <CardStack stack={content.careers} hideHeader />
      </div>
      <CalendarAlerts content={content.calendarAlerts} />
    </div>
  );
}
